import { useId, useState, type ReactNode } from "react";

import { cn } from "../cn";

export interface DisclosureProps {
  /** The row itself. Becomes the button's label, so keep it free of controls. */
  summary: ReactNode;
  /**
   * Shown under the summary whether or not the row is open — a duration bar, a
   * failure message. It is outside the button on purpose: a step's error is
   * the answer somebody came for, and putting it inside the trigger would make
   * the accessible name of every failed row carry a paragraph.
   */
  beneath?: ReactNode;
  /** What opening reveals. */
  children: ReactNode;
  /** Starts open. Uncontrolled after that. */
  defaultOpen?: boolean;
  /** A bare caret and no padding box, for a quiet "more options" row inside a form or sheet. */
  plain?: boolean;
  className?: string;
}

/**
 * A row that opens.
 *
 * There were three spellings of this before there was one. Rooms drew a
 * `<button aria-expanded>` inside a table cell and appended a second `<TR>`;
 * Requests used `<details>/<summary>` with `list-none` and a text `▸`; Live
 * events had no way to show a payload it had truncated. Three shapes for one
 * idea is how a console starts looking assembled rather than designed.
 *
 * A button rather than `<details>`, for two reasons that only showed up in
 * use. `<details>` cannot be animated open, and `list-none` deletes the
 * browser's own marker — so every use had to draw a caret anyway, and the one
 * that forgot shipped a row with no sign that it opened. And a `<summary>`
 * inside a grid row fights the grid: it is a layout box the spec puts
 * constraints on, and both call sites had already worked around it.
 *
 * The caret is `aria-hidden`. It says the same thing `aria-expanded` says, and
 * saying it twice is how a screen reader reads "collapsed triangle collapsed".
 */
export function Disclosure({
  summary,
  beneath,
  children,
  defaultOpen = false,
  plain = false,
  className,
}: DisclosureProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <div className={cn("flex flex-col", className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "group flex w-full items-center rounded-[var(--radius-sm)] text-left",
          plain ? "gap-2 py-1.5" : "gap-2.5 px-2 py-2",
          "transition-colors hover:bg-surface-2/60",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
          // The same `scroll-mt` `row-list.tsx`'s own `Row` carries, and for
          // the same reason — a keyboard focus or an anchor that scrolls this
          // button into view otherwise parks it flush under the chrome and a
          // group heading sticking below that. `Row`'s copy of this class does
          // nothing here: `summary` renders it with `role="presentation"`
          // *inside* this button, so this is the element scroll machinery
          // actually targets, and the one that has to carry the offset itself.
          // `--chrome-h` is how deep the chrome is — see `RowGroup`.
          "scroll-mt-[calc(var(--chrome-h,var(--header-h))+1.875rem)]",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "grid shrink-0 place-items-center text-micro text-ink-3 transition-transform",
            plain ? "size-3" : "size-4 rounded-[4px] border border-rule-strong",
            open && "rotate-90",
          )}
        >
          ▶
        </span>
        <span className="min-w-0 grow">{summary}</span>
      </button>

      {beneath === undefined ? null : (
        <div className={plain ? "pb-2" : "px-2 pb-2"}>{beneath}</div>
      )}

      <div id={panelId} hidden={!open} className={plain ? "pt-1 pb-3" : "px-2 pb-3"}>
        {open ? children : null}
      </div>
    </div>
  );
}
